import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { Logo } from '@/components/brand/Logo'
import { DotCluster, ConfluenceLines, GridRipple } from '@/components/brand/EchoArc'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card, CardTitle } from '@/components/ui/Card'
import { ConfidenceMeter } from '@/components/ui/ConfidenceMeter'
import type { ConfidenceBreakdown } from '@/lib/types'

const DEMO_CONFIDENCE: ConfidenceBreakdown = {
  score: 0.74,
  band: 'High',
  wilson: [0.64, 0.84],
  drivers: {
    evidence_volume: 0.85,
    evidence_relevance: 0.72,
    segment_coverage: 0.78,
    open_ended_depth: 0.68,
    pass_agreement: 0.86,
    productive_diversity: 0.4,
    prediction_diversity: 0.14,
    recency: 0.75,
    validation_history: 0.20,
    source_diversity: 0.70,
  },
  weak_signals: [],
}

const COLORS = [
  { name: 'Ink', hex: '#0A1628', role: 'Primary text (light), Primary bg (dark)' },
  { name: 'Slate', hex: '#1B2A3E', role: 'Secondary bg (dark), Headings (light)' },
  { name: 'Paper', hex: '#F8F4EC', role: 'Primary background (light)', border: true },
  { name: 'Paper Warm', hex: '#F0EBE0', role: 'Alternate surface (light)', border: true },
  { name: 'Echo Indigo', hex: '#1E2D5A', role: 'Logo mark, primary accent, buttons, links' },
  { name: 'Echo Ink', hex: '#1F3A56', role: 'Dark navy accent, secondary interactions' },
  { name: 'Citation Indigo', hex: '#3B4F7A', role: 'Citations, informational signals' },
  { name: 'Sage', hex: '#3F7558', role: 'Agreement, success, validated states' },
  { name: 'Earth Amber', hex: '#A36B1F', role: 'Caution, weak evidence, validate this' },
  { name: 'Crimson Earth', hex: '#9B2226', role: 'Danger, contradiction — use sparingly' },
  { name: 'Border', hex: '#D9D2C2', role: 'Borders, dividers', border: true },
  { name: 'Soft Panel', hex: '#EFE9DA', role: 'Card backgrounds, subtle surfaces', border: true },
]

export function BrandPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-16 space-y-16">
        <div>
          <h1 className="text-3xl font-medium text-deep-ink mb-2">Brand & Design System</h1>
          <p className="text-slate-500">Echosome visual identity — logos, palette, typography, and components.</p>
        </div>

        {/* Logo */}
        <section className="space-y-6">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Logo — Concept A: Anchored Echo</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Card className="flex flex-col items-center gap-6 py-10 bg-paper">
              <Logo variant="horizontal" size="lg" theme="light" />
              <Logo variant="horizontal" size="md" theme="light" />
              <Logo variant="horizontal" size="sm" theme="light" />
              <p className="text-xs text-slate-400">Light background</p>
            </Card>
            <Card className="flex flex-col items-center gap-6 py-10" style={{ backgroundColor: '#0A1628' }}>
              <Logo variant="horizontal" size="lg" theme="dark" />
              <Logo variant="horizontal" size="md" theme="dark" />
              <Logo variant="horizontal" size="sm" theme="dark" />
              <p className="text-xs text-slate-600">Dark background</p>
            </Card>
            <Card className="flex items-center justify-center gap-8 py-10">
              <Logo variant="icon" size="lg" />
              <Logo variant="icon" size="md" />
              <Logo variant="icon" size="sm" />
            </Card>
            <Card className="flex flex-col gap-3 py-6">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Logo rules</p>
              <ul className="text-xs text-slate-600 space-y-1.5">
                <li>• Mark: Echo Indigo #1E2D5A — single-hue arcs + anchor dot</li>
                <li>• Arcs: opacity 0.32 / 0.60 / 1.0 outer to inner</li>
                <li>• Wordmark: lowercase "echosome", Inter 500, -0.02em spacing</li>
                <li>• Do not split "echo" and "some" into different colors</li>
                <li>• Primary logo, header, favicon, all marketing surfaces</li>
              </ul>
            </Card>
          </div>
        </section>

        {/* Motif allocation */}
        <section className="space-y-6">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Motif Allocation</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Concept A', sub: 'Logo, header, favicon, all marketing', node: <Logo variant="icon" size="md" /> },
              { label: 'Concept C', sub: 'Echo Panel cards, segment visuals', node: <DotCluster count={16} size={48} /> },
              { label: 'Concept B', sub: 'Explainer diagrams, how-it-works flow', node: <ConfluenceLines /> },
              { label: 'Concept E', sub: 'Analytics, ensemble UI, calibration', node: <GridRipple active /> },
            ].map(m => (
              <Card key={m.label} className="flex flex-col items-center gap-3 py-6 text-center">
                {m.node}
                <p className="text-sm font-medium text-deep-ink">{m.label}</p>
                <p className="text-xs text-slate-500">{m.sub}</p>
              </Card>
            ))}
          </div>
        </section>

        {/* Color palette */}
        <section className="space-y-6">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Color Palette</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {COLORS.map(color => (
              <div key={color.hex} className="space-y-2">
                <div
                  className="h-16 rounded-lg"
                  style={{ backgroundColor: color.hex, border: color.border ? '1px solid #D9D2C2' : 'none' }}
                />
                <p className="text-xs font-medium text-deep-ink">{color.name}</p>
                <p className="text-xs text-slate-400 font-mono">{color.hex}</p>
                <p className="text-xs text-slate-500 leading-snug">{color.role}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section className="space-y-6">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Typography — Inter</h2>
          <div className="space-y-4">
            {[
              { label: 'Display / H1', className: 'text-4xl font-medium tracking-tight text-deep-ink', text: 'Turn past research into reusable echo panels.' },
              { label: 'H2', className: 'text-3xl font-medium tracking-tight text-deep-ink', text: 'Evidence-backed simulation' },
              { label: 'H3', className: 'text-xl font-medium text-deep-ink', text: 'Ensemble Pre-Read' },
              { label: 'Body', className: 'text-base text-slate-700', text: 'Upload surveys, interviews, open-ends, and transcripts. Echosome builds evidence-backed segment echoes.' },
              { label: 'Small / Caption', className: 'text-sm text-slate-500', text: 'Pass agreement · 6 of 7 passes agree Message B wins · Wilson 64–84%' },
              { label: 'Label / Code', className: 'text-xs font-mono text-slate-600', text: 'evidence_volume: 0.85 · segment_coverage: 0.78 · pass_agreement: 0.86' },
            ].map(t => (
              <div key={t.label} className="flex gap-4 py-3 border-b border-border-gray last:border-0">
                <span className="text-xs text-slate-400 w-28 shrink-0 mt-1">{t.label}</span>
                <p className={t.className}>{t.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Buttons */}
        <section className="space-y-6">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Buttons</h2>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary">Try the Echo Pre-Read Demo</Button>
            <Button variant="secondary">Upload a Study</Button>
            <Button variant="outline">Learn more</Button>
            <Button variant="ghost">Sign in</Button>
            <Button variant="danger">Delete</Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="primary" size="lg">Large Primary</Button>
            <Button variant="primary" size="sm">Small Primary</Button>
            <Button variant="primary" disabled>Disabled</Button>
          </div>
        </section>

        {/* Badges */}
        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Badges</h2>
          <div className="flex flex-wrap gap-2">
            <Badge variant="success">High confidence</Badge>
            <Badge variant="warning">Validate this</Badge>
            <Badge variant="danger">Low coverage</Badge>
            <Badge variant="demo">Demo data</Badge>
            <Badge variant="default">Segment Echo</Badge>
            <Badge variant="outline">Study Memory</Badge>
          </div>
        </section>

        {/* Confidence meter */}
        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Confidence Meter</h2>
          <Card className="max-w-md">
            <ConfidenceMeter confidence={DEMO_CONFIDENCE} groundTruthPresent={false} showDrivers={true} />
          </Card>
        </section>

        {/* Cards */}
        <section className="space-y-4">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Cards</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card padding="sm">
              <CardTitle>Evidence card (sm)</CardTitle>
              <p className="text-xs text-slate-500 mt-1">"Concrete explanation reduces anxiety." — General Homeowners</p>
            </Card>
            <Card padding="md">
              <CardTitle>Segment card (md)</CardTitle>
              <p className="text-xs text-slate-500 mt-1">842 respondents · High evidence strength</p>
            </Card>
            <Card padding="lg" shadow>
              <CardTitle>Report card (lg + shadow)</CardTitle>
              <p className="text-sm text-slate-600 mt-2">Message B wins overall with Medium-high confidence.</p>
            </Card>
          </div>
        </section>

        {/* Mobile layout note */}
        <section className="space-y-3">
          <h2 className="text-xl font-medium text-deep-ink border-b border-border-gray pb-3">Mobile Responsiveness</h2>
          <Card className="bg-soft-panel border-0">
            <p className="text-sm text-slate-600">All components are responsive at ≥ 360px. Tap targets are ≥ 44px on all primary actions. Sidebar collapses to drawer on mobile. Tables collapse to cards on narrow viewports.</p>
          </Card>
        </section>
      </main>
      <SiteFooter />
    </div>
  )
}
